export default defineNuxtPlugin((nuxtApp) => {
  const base = $fetch.create({ baseURL: '/api' })

  // Explicit wrapper instead of an onResponseError interceptor: ofetch
  // ignores interceptor return values, so an in-hook retry can never deliver
  // the retried response to the caller. (frontend ADR-0001)
  //
  // 403s deliberately receive no handling here — permission gating happens in
  // route middleware (meta.roles); residual 403s surface inline at the caller.
  const api = (async (request: Parameters<typeof base>[0], options?: Parameters<typeof base>[1]) => {
    try {
      return await base(request, options)
    } catch (error) {
      const status = (error as { statusCode?: number }).statusCode
      const url = typeof request === 'string' ? request : request.toString()

      // An assume session that expired fails closed on every endpoint. It
      // cannot be refreshed: end it and return to the admin's own session.
      const code = (error as { data?: { error?: string } }).data?.error
      // The stop call itself is excluded: stopAssume would wait on itself.
      if (status === 401 && code === 'assume_expired' && !url.includes('/auth/assume/stop')) {
        const session = await nuxtApp.runWithContext(() => useSession())
        try {
          await session.stopAssume()
        } catch {
          // The original assume_expired error is what the caller needs to see.
        }
        throw error
      }

      // Auth endpoints surface their own 401s to the caller (login failures,
      // refresh/logout must not recurse).
      if (status !== 401 || url.includes('/auth/')) throw error

      const session = await nuxtApp.runWithContext(() => useSession())
      const outcome = await session.refresh()
      // 'refreshed' or 'skipped' both mean cookies are fresh — retry once and
      // return the result. A 401 rejection means the handler never ran, so
      // retrying mutations is safe.
      if (outcome === 'refreshed' || outcome === 'skipped') {
        return await base(request, options)
      }

      // Refresh genuinely failed: the session is gone.
      await nuxtApp.runWithContext(async () => {
        const route = useRouter().currentRoute.value
        if (!route.meta.public) {
          await navigateTo(`/login?redirect=${encodeURIComponent(route.fullPath)}`)
        }
      })
      throw error
    }
  }) as typeof base

  return { provide: { api } }
})
